import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Button,
  Popover,
  List,
  MenuItem,
  ListSubheader,
  TextField,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import { FaCode, FaSearch, FaTimes } from 'react-icons/fa';
import { CATEGORIAS_VARIABLES_CONTRATO } from '../../../constantes/variablesContrato';

/**
 * Normaliza cadenas de texto para búsquedas insensibles a mayúsculas y acentos.
 */
const normalizarTexto = (texto) =>
  (texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const MenuVariables = ({ classes, onInsertarVariable, onGuardarSeleccion }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [filtroVariable, setFiltroVariable] = useState('');
  const inputBuscadorRef = useRef(null);

  const abrirMenu = (e) => {
    if (onGuardarSeleccion) onGuardarSeleccion();
    setAnchorEl(e.currentTarget);
    setFiltroVariable('');
  };

  const cerrarMenu = () => {
    setAnchorEl(null);
  };

  // Enfocar automáticamente el campo de búsqueda al abrir el popover
  useEffect(() => {
    if (anchorEl) {
      const timer = setTimeout(() => {
        if (inputBuscadorRef.current) {
          inputBuscadorRef.current.focus();
        }
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [anchorEl]);

  const seleccionarVariable = (clave) => {
    onInsertarVariable(clave);
    cerrarMenu();
  };

  // Filtrado reactivo insensible a mayúsculas, acentos y con soporte para términos múltiples
  const categoriasFiltradas = useMemo(() => {
    const terminos = normalizarTexto(filtroVariable).split(/\s+/).filter(Boolean);

    if (terminos.length === 0) {
      return CATEGORIAS_VARIABLES_CONTRATO;
    }

    return CATEGORIAS_VARIABLES_CONTRATO.map((cat) => {
      const variables = cat.variables.filter((v) => {
        const labelNorm = normalizarTexto(v.label);
        const claveNorm = normalizarTexto(v.clave);
        return terminos.every(
          (termino) => labelNorm.includes(termino) || claveNorm.includes(termino)
        );
      });
      return { ...cat, variables };
    }).filter((cat) => cat.variables.length > 0);
  }, [filtroVariable]);

  const handleKeyDownBuscador = (e) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      cerrarMenu();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const primerResultado = categoriasFiltradas[0]?.variables[0];
      if (primerResultado) {
        seleccionarVariable(primerResultado.clave);
      }
    }
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
      <Button
        variant="contained"
        size="small"
        className={classes.insertVariableBtn}
        startIcon={<FaCode size={13} />}
        onMouseDown={(e) => e.preventDefault()}
        onClick={abrirMenu}
      >
        Insertar Variable
      </Button>

      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={cerrarMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            maxHeight: 460,
            width: 360,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        {/* Buscador fijo en la parte superior */}
        <div
          className={classes.searchField}
          style={{ backgroundColor: '#ffffff', flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDownBuscador}
        >
          <TextField
            inputRef={inputBuscadorRef}
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Buscar variable..."
            value={filtroVariable}
            onChange={(e) => setFiltroVariable(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch size={12} color="#718096" />
                </InputAdornment>
              ),
              endAdornment: filtroVariable ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFiltroVariable('');
                      inputBuscadorRef.current?.focus();
                    }}
                    edge="end"
                    style={{ padding: 4 }}
                    title="Limpiar búsqueda"
                  >
                    <FaTimes size={11} color="#a0aec0" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </div>

        {/* Listado de variables scrolleable */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <List disablePadding>
            {categoriasFiltradas.length === 0 ? (
              <MenuItem disabled style={{ fontSize: 13, justifyContent: 'center', padding: '16px 8px' }}>
                No se encontraron variables
              </MenuItem>
            ) : (
              categoriasFiltradas.map((cat) => (
                <React.Fragment key={`cat-${cat.categoria}`}>
                  <ListSubheader
                    disableSticky
                    className={classes.menuItemCategory}
                  >
                    {cat.categoria}
                  </ListSubheader>
                  {cat.variables.map((v) => (
                    <MenuItem
                      key={v.clave}
                      className={classes.menuItemVariable}
                      onClick={() => seleccionarVariable(v.clave)}
                    >
                      <span>{v.label}</span>
                      <span className={classes.variableTagPreview}>
                        &#123;&#123;{v.clave}&#125;&#125;
                      </span>
                    </MenuItem>
                  ))}
                </React.Fragment>
              ))
            )}
          </List>
        </div>
      </Popover>
    </div>
  );
};

export default React.memo(MenuVariables);
